import { Link, PrimaryButton, Spinner, SpinnerSize } from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import "./DPRProforma.css";
import { makePdf } from "../PrintQuotation/PrintQuotation";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { QuotationContext } from "../QuotationContext";
import { QuotationInfoType } from "../Quotation.types";
import { CountryProfileService } from "../../../services/CountryProfiles/CountryProfileService";
import { useNavigate } from "react-router-dom";
import {
  priceWithCurrency,
  priceWithCurrencyForJapan,
} from "../../../Utils/PriceWithCurrency";
import { CountryProfileInfo } from "../../CountriesProfile/CountriesProfile.type";
import { IExtendedDetailItemEntity } from "../Common/Interfaces/IItemEntity";
import {
  formattedPrice,
  formattedPriceForJapan,
} from "../../../Utils/FormattedPrice";
import {
  formatDateWithPattern,
  getCountryDateFormat,
} from "../../../Utils/DateFormatUtility";
import {
  getDPRProformaLabels,
  getDefaultLanguage,
  getAvailableLanguages,
} from "../../../common/constants/QuoteTemplateLabels";
import { hasWarrantyItems } from "../../../Utils/QuotationUtils";

// Warranty Consent Messages Component
interface WarrantyConsentMessagesProps {
  country: string | undefined;
  hasWarranty: boolean;
}

const WarrantyConsentMessages: React.FC<WarrantyConsentMessagesProps> = ({ country, hasWarranty }) => {
  if (!hasWarranty) return null;

  return (
    <>
      {country === "Austria" && (
        <p style={{ marginTop: "15px" }}>
          <strong>Austria Electronic (E)Consent:</strong> I hereby expressly give my consent to receive documentation and communications associated with the Microsoft Protection Plan through electronic means. I can at any time revoke such consent or provide another e-mail address to be used. Likewise, the insurer has the right to revoke its consent to e-communication. Both parties to the insurance contract still have the right to send declarations, communications and information on paper. I have the right to ask for hardcopies of any declarations or information received electronically once free of charge.
        </p>
      )}
      {country === "Italy" && (
        <p style={{ marginTop: "15px" }}>
          <strong>Italy Terms & Conditions (T&C) Consent:</strong> "I confirm that I have read, and I agree to the Terms and Conditions of the Microsoft Protection Plan".
        </p>
      )}
    </>
  );
};

// Helper function for country-specific date formatting
const formatDateForCountry = async (
  dateStr: string | undefined,
  countryName: string | undefined,
): Promise<string> => {
  if (!dateStr) return "";

  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateStr}`);
      return dateStr || "";
    }

    let dateFormat = "DD/MM/YYYY"; // Default format
    if (countryName) {
      const countryFormat = await getCountryDateFormat(countryName);
      if (countryFormat) {
        dateFormat = countryFormat;
      }
    }

    return formatDateWithPattern(date, dateFormat);
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateStr || "";
  }
};

interface GenericDPRProformaProps {
  country: string;
  selectedLanguage?: string;
}

export const GenericDPRProforma: React.FunctionComponent<
  GenericDPRProformaProps
> = ({ country, selectedLanguage }) => {
  const navigate = useNavigate();
  const appInsights = useAppInsightsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [termsAndConditionsDocument, settermsAndConditionsDocument] = useState(
    new Uint8Array(),
  );
  const [termsAndConditionsDocumentId, settermsAndConditionsDocumentId] =
    useState("");
  const [countryProfileInfo, setCountryProfileInfo] =
    useState<CountryProfileInfo>();
  const { quotationInfo } = useContext(QuotationContext) as QuotationInfoType;

  // Language state management with country-specific defaults
  // Read from localStorage first to get the most up-to-date value set by QuotationDetail
  const [currentLanguage, setCurrentLanguage] = useState<string>(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    return storedLanguage || selectedLanguage || getDefaultLanguage(country);
  });

  // Sync currentLanguage with selectedLanguage prop when it changes
  // Also check localStorage for the most up-to-date value
  // This fixes the issue where the language switcher selection wasn't being applied
  // when first opening DPR Proforma from the main search page
  useEffect(() => {
    const storedLanguage = localStorage.getItem("selectedLanguage");
    const languageToUse = storedLanguage || selectedLanguage;
    if (languageToUse) {
      setCurrentLanguage(languageToUse);
    }
  }, [selectedLanguage]);

  // Get available languages for this country
  const availableLanguages = getAvailableLanguages(country);

  // Get labels for the specific country and language (fr=French, en=English)
  const labels = getDPRProformaLabels(country, currentLanguage);

  // State for formatted dates
  const [formattedQuotationDate, setFormattedQuotationDate] =
    useState<string>("");

  // Format dates according to country's preferred date format
  useEffect(() => {
    const formatDates = async () => {
      try {
        if (quotationInfo?.quotationDate) {
          const formatted = await formatDateForCountry(
            quotationInfo.quotationDate,
            country,
          );
          setFormattedQuotationDate(formatted);
        }
      } catch (error) {
        console.error("Error formatting dates:", error);
      }
    };

    formatDates();
  }, [quotationInfo?.quotationDate, country]);

  const lineItems: IExtendedDetailItemEntity[] =
    quotationInfo?.bundleItems?.length > 0
      ? ([
          ...quotationInfo?.standaloneItems,
          ...quotationInfo?.bundleItems,
        ] as IExtendedDetailItemEntity[])
      : quotationInfo?.standaloneItems?.length > 0
        ? ([
            ...quotationInfo?.standaloneItems,
            ...quotationInfo.bundleItems,
          ] as IExtendedDetailItemEntity[])
        : [];

  // Check if cart contains warranty items
  const hasWarranty = hasWarrantyItems(lineItems);

  const pdfFileName = `${quotationInfo?.quotationNumber}_${country}_DPR_Proforma`;

  useEffect(() => {
    if (quotationInfo?.customer?.country) {
      getCountryProfile(quotationInfo.customer.country);
    }
  }, [quotationInfo?.customer?.country]);

  const getCountryProfile = async (countryName: string) => {
    setIsLoading(true);
    CountryProfileService.getCountryProfileByCountryName(countryName)
      .then(async (res) => {
        if (res !== undefined) {
          settermsAndConditionsDocumentId(
            res.data.data.data.termsAndConditions[
              res.data.data.data.termsAndConditions?.length - 1
            ]?.id,
          );
          setCountryProfileInfo(res.data.data.data);
          appInsights.trackEvent({
            name: `In GenericDPRProforma.tsx, country detail fetched successfully for ${country}.`,
          });
          setIsLoading(false);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        appInsights.trackException({
          error,
          properties: {
            name: `In GenericDPRProforma.tsx, error while getting country detail for ${country}.`,
          },
        });
      });
  };

  // Effect to trigger PDF generation when terms and conditions document is loaded
  useEffect(() => {
    async function PrintPdf() {
      try {
        if (termsAndConditionsDocument.byteLength > 0) {
          setIsLoading(true);
          await makePdf(
            appInsights,
            pdfFileName,
            lineItems.length,
            termsAndConditionsDocument
          );
          setIsLoading(false);
          settermsAndConditionsDocument(new Uint8Array());
        }
      } catch (error) {
        setIsLoading(false);
      }
    }
    PrintPdf();
  }, [termsAndConditionsDocument.byteLength]);

  const PrintHtmlToPdf = async () => {
    appInsights.trackEvent({
      name: `In GenericDPRProforma.tsx, Clicked Print button to download the ${country} DPR Proforma`,
    });
    if (
      termsAndConditionsDocument.byteLength === 0 &&
      termsAndConditionsDocumentId
    ) {
      await CountryProfileService.getCountryProfileDocument(
        termsAndConditionsDocumentId,
      )
        .then((res) => {
          appInsights.trackEvent({
            name: `In GenericDPRProforma.tsx, Country Terms and Condition document fetched successfully for ${country}.`,
          });
          if (res.data === null) {
            makePdf(
              appInsights,
              pdfFileName,
              lineItems.length,
              termsAndConditionsDocument,
            );
          }
          settermsAndConditionsDocument(res.data);
        })
        .catch((error) => {
          makePdf(
            appInsights,
            pdfFileName,
            lineItems.length,
            termsAndConditionsDocument,
          );
          appInsights.trackException({
            error,
            properties: {
              name: `In GenericDPRProforma.tsx, error while fetching termsAndCondition document for ${country}.`,
            },
          });
        });
    } else {
      makePdf(
        appInsights,
        pdfFileName,
        lineItems.length,
        termsAndConditionsDocument,
      );
    }
  };

  const backButtonClick = () => {
    navigate(-1);
  };

  if (isLoading) {
    return <Spinner size={SpinnerSize.large} />;
  }

  return (
    <div
      className={`generic-dpr-template country-${country.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {quotationInfo ? (
        <>
          <div className="btn_download">
            <PrimaryButton className="margin-right5" onClick={backButtonClick}>
              {labels.buttons.back}
            </PrimaryButton>
            <PrimaryButton onClick={PrintHtmlToPdf}>
              {labels.buttons.downloadPdf}
            </PrimaryButton>
          </div>
          <div style={{ padding: "0 40px" }}>
            <div id={pdfFileName}>
              {/* Header Section */}
              <div
                className={`flex space-between ${window.innerWidth > 750 ? "flexDirectionRow" : "flexDirectionColumn"} width100`}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: country === "Japan" ? "30px" : undefined,
                  }}
                >
                  <div>
                    <img
                      src="/image.png"
                      className="invlogo"
                      style={{ width: "150px", marginBottom: "10px" }}
                      alt="Microsoft logo"
                    />
                      <p className="pb-1">
                        {countryProfileInfo?.administrativeOriginName}
                      </p>
                      <p>{countryProfileInfo?.administrativeOriginStreet}</p>
                      <p className="pb-1">
                        {countryProfileInfo?.administrativeOriginCity}
                        {countryProfileInfo?.administrativeOriginState
                          ? `, ${countryProfileInfo.administrativeOriginState} `
                          : " "}
                        {countryProfileInfo?.administrativeOriginCountry ===
                        "Singapore"
                          ? ""
                          : countryProfileInfo?.administrativeOriginZipCode}
                      </p>
                      <p className="pb-1">
                        {countryProfileInfo?.administrativeOriginCountry}
                        {countryProfileInfo?.administrativeOriginCountry ===
                        "Singapore"
                          ? ` ${countryProfileInfo?.administrativeOriginZipCode}`
                          : ""}
                      </p>
                      {countryProfileInfo?.vatId && (
                        <p className="pb-1">
                          <b>{labels.dprDetails.vatRegistration}:</b>
                          {` ${countryProfileInfo?.vatId}`}
                        </p>
                      )}
                  </div>
                </div>
                <div style={{ marginTop: "10px" }}>
                  <div>
                    <h2
                      className="pb-1"
                      style={{ textAlign: "right", fontSize: "24px" }}
                    >
                      {labels.downpaymentRequest}
                    </h2>
                  </div>
                  <div>
                    <p className="pb-1">
                      <b>{labels.dprDetails.requestDate}:</b>
                        {` ${formattedQuotationDate || quotationInfo?.quotationDate}`}
                      </p>
                    <p className="pb-1">
                      <b>{labels.dprDetails.quotationNumber}:</b>
                      {` ${quotationInfo?.quotationNumber}`}
                    </p>
                    <p className="pb-1">
                      <b>{labels.customerInfo.customerPo}:</b>
                      {` ${
                        quotationInfo?.customerPurchaseOrder
                          ? quotationInfo?.customerPurchaseOrder
                          : ""
                      }`}
                    </p>
                    <p className="pb-1">
                      <b>{labels.dprDetails.pageNumber}</b>
                      {` 1 ${labels.dprDetails.of} 1`}
                    </p>
                  </div>
                </div>
              </div>

              <div className="horizontalLine"></div>
              <div className={`top30 flex space-between ${window.innerWidth > 750 ? 'flexDirectionRow' : 'flexDirectionColumn'}`}>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <table className="table-no-border">
                    <tbody>
                      <tr>
                        <td className="td-border">
                          <p className="pb-1">
                            <b>{labels.customerInfo.billTo}:</b>
                          </p>
                          <p className="pb-1">
                            {`${quotationInfo?.customer?.selectedBillToAddress?.name}`}
                          </p>
                          <p className="pb-1">
                            {`${quotationInfo?.customer?.selectedBillToAddress?.street}`}
                          </p>
                          <p className="pb-1">
                            {`${quotationInfo?.customer?.selectedBillToAddress?.city}, ${quotationInfo?.customer?.selectedBillToAddress?.state}${quotationInfo?.customer?.selectedBillToAddress?.country === "Singapore" ? "" : " " + quotationInfo?.customer?.selectedBillToAddress?.postalCode}`}
                          </p>
                          <p className="pb-1">
                            {quotationInfo?.customer?.selectedBillToAddress?.country}
                            {quotationInfo?.customer?.selectedBillToAddress?.country ===
                    "Singapore"
                      ? ` ${quotationInfo?.customer?.selectedBillToAddress?.postalCode}`
                      : ""}
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <p className="pb-1">
                    <b>{labels.customerInfo.customer}:</b>
                    {` ${country === "France" ? quotationInfo?.customer?.selectedBillToAddress?.name : quotationInfo?.customer?.accountId}`}
                    </p>
                  <p className="pb-1">
                    <b>
                      {currentLanguage === "fr" || currentLanguage === "ja"
                        ? labels.customerInfo.customerTaxId
                        : `Customer ${lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : "Tax"} ID`}:
                    </b>
                    {` ${quotationInfo?.customer?.taxId}`}
                  </p>
                  <p className="pb-1">
                    <b>{labels.customerInfo.soldToId}:</b>
                    {` ${quotationInfo?.customer?.businessAddress?.sapAccountId || ""}`}
                  </p>
                </div>
                <div className={`${window.innerWidth > 750 ? 'width33' : ''}`}>
                  <table className="table-no-border">
                    <tbody>
                      <tr>
                        <td className="td-border">
                          <p className="pb-1">
                            <b>{labels.customerInfo.shipTo}:</b>
                          </p>
                          <p className="pb-1">
                            {`${quotationInfo?.customer?.selectedShipToAddress?.name}`}
                          </p>
                          <p className="pb-1">
                            {quotationInfo?.customer?.selectedShipToAddress?.street}
                          </p>
                          <p className="pb-1">
                            {`${quotationInfo?.customer?.selectedShipToAddress?.city}, ${quotationInfo?.customer?.selectedShipToAddress?.state}${quotationInfo?.customer?.selectedShipToAddress?.country === "Singapore" ? "" : " " + quotationInfo?.customer?.selectedShipToAddress?.postalCode}`}
                          </p>
                          <p className="pb-1">
                            {quotationInfo?.customer?.selectedShipToAddress?.country}
                            {quotationInfo?.customer?.selectedShipToAddress?.country ===
                    "Singapore"
                      ? ` ${quotationInfo?.customer?.selectedShipToAddress?.postalCode}`
                      : ""}
                          </p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Product Table */}
              <div className="row" style={{ marginTop: "50px" }}>
                <table className="table border-color">
                  <tr className="table-header">
                    <th>#</th>
                    <th>{labels.productTable.description}</th>
                    <th>{labels.productTable.quantity}</th>
                    {lineItems[0]?.taxType ? (
                      <th className="text-right">{labels.productTable.Price}</th>
                    ) : (
                      <th className="text-right">{labels.productTable.msrp}</th>
                    )}
                    {lineItems[0]?.taxType ? (
                      <th className="text-right">
                        {currentLanguage === "fr" || currentLanguage === "ja"
                          ? labels.productTable.extendedLineTax
                          : `Extended Line ${lineItems[0]?.taxType}`}
                      </th>
                    ) : (
                      <th className="text-right">{labels.productTable.extendedLineTax}</th>
                    )}
                    {quotationInfo?.customer?.region === "EMEA" && (
                      <th className="text-right">
                        {labels.productTable.extendedCopyrightLevyFee}
                        {Number(quotationInfo?.totalFee ?? 0) > 0 ? "*" : ""}
                      </th>
                    )}
                    <th className="text-right">
                      {labels.productTable.lineAmount}
                    </th>
                  </tr>
                  {lineItems?.map((product, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        [{product.sapItemId}]{product.productTitle}
                      </td>
                      <td>{product.quantity}</td>
                      <td className="text-right">
                        {country === "Japan"
                          ? formattedPriceForJapan(product?.msrp)
                          : formattedPrice(product?.msrp)}
                      </td>
                      <td className="text-right">
                        {country === "Japan"
                          ? formattedPriceForJapan(product?.lineItemTax)
                          : formattedPrice(product?.lineItemTax)}
                      </td>
                      {quotationInfo?.customer?.region === "EMEA" && (
                        <td className="text-right">
                          {country === "Japan"
                            ? formattedPriceForJapan(product?.levyFeeAmount)
                            : formattedPrice(product?.levyFeeAmount)}
                        </td>
                      )}
                      <td className="text-right">
                        {country === "Japan"
                          ? formattedPriceForJapan(product?.lineTotalWithTax)
                          : formattedPrice(product?.lineTotalWithTax)}
                      </td>
                    </tr>
                  ))}
                </table>
              </div>
              <div className="below-table">
                <div>
                  {quotationInfo?.customer?.region === "EMEA" && (
                    <tr>
                      <td colSpan={6} className="tDataWithoutBorder">
                        <strong>{labels.summary.copyrightLevyFee}:</strong>
                      </td>
                      <td className="text-right tDataWithoutBorder">
                        {country === "Japan"
                          ? priceWithCurrencyForJapan(
                              quotationInfo?.quoteCurrency,
                              quotationInfo.totalFee ?? 0,
                            )
                          : priceWithCurrency(
                              quotationInfo?.quoteCurrency,
                              quotationInfo.totalFee ?? 0,
                            )}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td colSpan={6} className="tDataWithoutBorder">
                      <strong>
                        {["Japan", "New Zealand", "Singapore"].includes(country)
                          ? `${labels.summary.subtotal}:`
                          : `${labels.summary.subtotalWithLevy || labels.summary.subtotal}:`}
                      </strong>
                    </td>
                    <td className="text-right tDataWithoutBorder">
                      {country === "Japan"
                        ? priceWithCurrencyForJapan(
                            quotationInfo?.quoteCurrency,
                            quotationInfo.subTotal,
                          )
                        : priceWithCurrency(
                            quotationInfo?.quoteCurrency,
                            quotationInfo.subTotal,
                          )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={6} className="tDataWithoutBorder">
                      <strong>
                        {currentLanguage === "fr" || currentLanguage === "ja"
                          ? labels.summary.totalTax
                          : `${lineItems.length > 0 && lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Amount (${lineItems.length > 0 && lineItems[0]?.taxType !== null ? lineItems[0]?.taxType : 'Tax'} Rate)`}:
                      </strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {country === "Japan"
                        ? priceWithCurrencyForJapan(
                            quotationInfo?.quoteCurrency,
                            quotationInfo?.totalTax
                              ? quotationInfo?.totalTax
                              : 0,
                          )
                        : priceWithCurrency(
                            quotationInfo?.quoteCurrency,
                            quotationInfo?.totalTax
                              ? quotationInfo?.totalTax
                              : 0,
                          )}{" "}
                      (
                      {(lineItems.find((item) => item?.effectiveTaxRate > 0)
                        ?.effectiveTaxRate || 0) * 100}
                      %)
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={6} className="tDataWithoutBorder">
                      <strong>{labels.summary.grandTotal}{country === "Singapore" ? " (inclusive of GST)" : ""}:</strong>
                    </td>
                    <td colSpan={1} className="text-right tDataWithoutBorder">
                      {country === "Japan"
                        ? priceWithCurrencyForJapan(
                            quotationInfo?.quoteCurrency,
                            quotationInfo.total,
                          )
                        : priceWithCurrency(
                            quotationInfo?.quoteCurrency,
                            quotationInfo.total,
                          )}
                    </td>
                  </tr>
                </div>
              </div>

              <div
                style={{
                  marginTop: "50px",
                  display: "inline-flex",
                }}
              >
                <p>
                  <strong className="pb-5">{labels.footer.comments}:</strong>
                  <p>{quotationInfo?.comments}</p>
                  {labels.footer.paymentAppreciation}
                </p>
              </div>
              {quotationInfo?.paymentMethod === "WireTransfer" ? (
                <>
                  <div style={{ marginTop: "10px" }}>
                    <p className="pb-5">
                      <strong>{labels.payment.wireTransferOption}</strong>
                    </p>
                  </div>
                  <div>
                    <div>
                      <div>
                        {countryProfileInfo?.bankName && (
                          <p className="pb-5">
                            <b>{labels.payment.bankName}</b>
                            {`${countryProfileInfo?.bankName}`}
                          </p>
                        )}
                        {countryProfileInfo?.bankAddress && (
                          <p className="pb-5">
                            <b>{labels.payment.bankAddress}</b>
                            {`${countryProfileInfo?.bankAddress}`}
                          </p>
                        )}
                        {countryProfileInfo?.bankPhone && (
                          <p className="pb-5">
                            <b>{labels.payment.bankPhone}</b>
                            {`${countryProfileInfo?.bankPhone}`}
                          </p>
                        )}
                        {countryProfileInfo?.addressLine1 && (
                          <p className="pb-5">
                            <b>{labels.payment.addressLine1}</b>
                            {`${countryProfileInfo?.addressLine1}`}
                          </p>
                        )}
                        {countryProfileInfo?.addressLine2 && (
                          <p className="pb-5">
                            <b>{labels.payment.addressLine2}</b>
                            {`${countryProfileInfo?.addressLine2}`}
                          </p>
                        )}
                        {countryProfileInfo?.addressLine3 && (
                          <p className="pb-5">
                            <b>{labels.payment.addressLine3}</b>
                            {`${countryProfileInfo?.addressLine3}`}
                          </p>
                        )}
                        {countryProfileInfo?.accountName && (
                          <p className="pb-5">
                            <b>{labels.payment.accountName}</b>
                            {`${countryProfileInfo?.accountName}`}
                          </p>
                        )}
                        {countryProfileInfo?.accountAddress && (
                          <p className="pb-5">
                            <b>{labels.payment.accountAddress}</b>
                            {`${countryProfileInfo?.accountAddress}`}
                          </p>
                        )}
                        {countryProfileInfo?.accountNumber && (
                          <p className="pb-5">
                            <b>{labels.payment.accountNumber}</b>
                            {`${countryProfileInfo?.accountNumber}`}
                          </p>
                        )}
                        {countryProfileInfo?.iban && (
                          <p className="pb-5">
                            <b>{labels.payment.ibanNumber}</b>
                            {`${countryProfileInfo?.iban}`}
                          </p>
                        )}
                        {countryProfileInfo?.swiftCode && (
                          <p className="pb-5">
                            <b>{labels.payment.swiftCode}</b>
                            {`${countryProfileInfo?.swiftCode}`}
                          </p>
                        )}
                        {countryProfileInfo?.swiftBicCode && (
                          <p className="pb-5">
                            <b>{labels.payment.swiftBicCode}</b>
                            {`${countryProfileInfo?.swiftBicCode}`}
                          </p>
                        )}
                        {countryProfileInfo?.switchCode && (
                          <p className="pb-5">
                            <b>{labels.payment.switchCode}</b>
                            {`${countryProfileInfo?.switchCode}`}
                          </p>
                        )}
                        {countryProfileInfo?.sortCode && (
                          <p className="pb-5">
                            <b>{labels.payment.sortCode}</b>
                            {`${countryProfileInfo?.sortCode}`}
                          </p>
                        )}
                        {countryProfileInfo?.correspondingBank && (
                          <p className="pb-5">
                            <b>{labels.payment.correspondingBank}</b>
                            {`${countryProfileInfo?.correspondingBank}`}
                          </p>
                        )}
                        {countryProfileInfo?.vatId && (
                          <p className="pb-5">
                            <b>{labels.payment.vat}</b>
                            {`${countryProfileInfo?.vatId}`}
                          </p>
                        )}
                        {countryProfileInfo?.correspondingBankSwiftBICCode && (
                          <p className="pb-5">
                            <b>
                              {labels.payment.correspondingBankSwiftBicCode}
                            </b>
                            {`${countryProfileInfo?.correspondingBankSwiftBICCode}`}
                          </p>
                        )}
                        {countryProfileInfo?.bankKey && (
                          <p className="pb-5">
                            <b>{labels.payment.bankKey}</b>
                            {`${countryProfileInfo?.bankKey}`}
                          </p>
                        )}
                        {countryProfileInfo?.bcCode && (
                          <p className="pb-5">
                            <b>{labels.payment.bcCode}</b>
                            {`${countryProfileInfo?.bcCode}`}
                          </p>
                        )}
                        {countryProfileInfo?.remittanceCurrencyCode && (
                          <p className="pb-5">
                            <b>{labels.payment.currencyCode}</b>
                            {`${countryProfileInfo?.remittanceCurrencyCode}`}
                          </p>
                        )}
                        {countryProfileInfo?.remittanceCompanyCode && (
                          <p className="pb-5">
                            <b>{labels.payment.companyCode}</b>
                            {`${countryProfileInfo?.remittanceCompanyCode}`}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="pb-1">{labels.payment.remittanceNote}</p>
                  </div>
                  <div style={{ marginTop: "10px", paddingBottom: "50px" }}>
                    <div>
                      <div>
                        <p className="pb-5">
                          <strong>{labels.footer.termsConditions}</strong>
                        </p>
                        <p className="pb-1">
                          {labels.footer.termsInstructionsWireTransfer}&nbsp;
                          <a href="#">{labels.footer.supportEmail}</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginTop: "10px" }}>
                    <div>
                      <div>
                        <p className="pb-1">
                          <b>{labels.footer.creditCardTitle}</b>
                        </p>
                        <p className="pb-1">
                          {labels.footer.creditCardInstructions} &nbsp;
                          <Link
                            onClick={() => {
                              const url = quotationInfo.adyenPaymentLink;
                              const formattedUrl = /^https?:\/\//.test(url)
                                ? url
                                : `https://${url}`;
                              window.open(
                                formattedUrl,
                                "_blank",
                                "noopener,noreferrer",
                              );
                            }}
                            target="_blank"
                            underline
                          >
                            {quotationInfo.adyenPaymentLink}
                          </Link>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: "10px" }}>
                    <div>
                      <div>
                        <p className="pb-1">
                          <b>{labels.footer.termsConditions}</b>
                        </p>
                        <p className="pb-1">
                          {labels.footer.termsInstructionsCreditCard} &nbsp;
                          <a href="#">{labels.footer.supportEmail}</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {/* Warranty Consent Messages - rendered once for both payment methods */}
              <WarrantyConsentMessages country={country} hasWarranty={hasWarranty} />
            </div>
          </div>
        </>
      ) : (
        <Spinner size={SpinnerSize.large} />
      )}
    </div>
  );
};
